import { useEffect, useRef } from 'react';
import scrollBehavior from '@/shared/utils/scrollBehavior';

/**
 * After a wizard changes step, bring the new step's top into view and put
 * focus on its heading. Without this, Next and Previous leave the page
 * scrolled to wherever the old step's buttons were, which on a phone is the
 * bottom of the new step.
 *
 * topRef marks where the step starts (the step indicator); contentRef wraps
 * the step's content, whose first h2 gets focus. If focus is already inside
 * the new step (a caller sent it to a specific field), it stays there.
 */
export default function useStepChangeFocus(step, topRef, contentRef) {
    const shownStep = useRef(step);

    useEffect(() => {
        if (shownStep.current === step) return;
        shownStep.current = step;

        const content = contentRef.current;
        if (!content) return;
        // React can reuse the previous step's heading node, so focus left on
        // it does not count as the caller choosing a field
        const heading = content.querySelector('h2');
        const active = document.activeElement;
        if (content.contains(active) && active !== heading) return;

        topRef.current?.scrollIntoView({
            block: 'start',
            behavior: scrollBehavior(),
        });

        if (heading) {
            heading.setAttribute('tabindex', '-1');
            // preventScroll, so focusing does not cut the smooth scroll short;
            // the heading sits just below topRef, which that scroll reveals.
            heading.focus({ preventScroll: true });
        }
    }, [step, topRef, contentRef]);
}
